/* SuspendDialog — admin suspension modal.
 * Frontend policy: expires_at is REQUIRED (no indefinite suspension
 * via UI). Reason is REQUIRED (1..1000 chars).
 */
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';

const DEFAULT_DAYS = 7;

function defaultExpiresAt() {
  const d = new Date();
  d.setDate(d.getDate() + DEFAULT_DAYS);
  return d.toISOString().slice(0, 16); // datetime-local format
}

export function SuspendDialog({ member, onClose, onSubmit }) {
  const [reason,      setReason]      = useState('');
  const [expiresAt,   setExpiresAt]   = useState(defaultExpiresAt());
  const [isEmergency, setIsEmergency] = useState(false);
  const [busy,        setBusy]        = useState(false);

  const trimmedReason = reason.trim();
  const canSubmit = trimmedReason.length > 0 && !!expiresAt;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      const iso = new Date(expiresAt).toISOString();
      await onSubmit({
        targetUserId: member.user_id, reason: trimmedReason,
        expiresAt: iso, isEmergency,
      });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="emp-suspend-dialog">
        <DialogHeader>
          <DialogTitle>Suspend member</DialogTitle>
          <DialogDescription className="font-mono text-xs">{member.user_id}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="emp-suspend-reason">Reason (required)</Label>
            <Textarea
              id="emp-suspend-reason"
              required maxLength={1000} value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Governance reason for the suspension"
              rows={3}
              data-testid="emp-suspend-reason-input"
            />
            <p className="mt-1 text-xs text-slate-300">{reason.length}/1000</p>
          </div>
          <div>
            <Label htmlFor="emp-suspend-expires">Expires at (required)</Label>
            <Input
              id="emp-suspend-expires"
              type="datetime-local" required
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              data-testid="emp-suspend-expires-input"
            />
            <p className="mt-1 text-xs text-slate-300">
              The shim refuses indefinite suspension via the UI.
            </p>
          </div>
          <Label className="flex cursor-pointer items-center gap-2 text-sm">
            <Checkbox checked={isEmergency} onCheckedChange={setIsEmergency}
              data-testid="emp-suspend-emergency-checkbox" />
            Emergency suspension (records as <code>member.suspended_emergency</code>)
          </Label>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}
              data-testid="emp-suspend-cancel">Cancel</Button>
            <Button type="submit" disabled={!canSubmit || busy}
              variant="destructive"
              data-testid="emp-suspend-submit">
              {busy ? 'Suspending…' : 'Suspend'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
